import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../../Services/AuthService';
import PropertiesService from '../../Services/PropertiesService';
import userProfile from '../../assets/Images/Navbar/user.png';
import { User, Lock, Camera, ArrowLeft, Check, Eye, EyeOff, Loader } from 'lucide-react';
import './ProfilePage.css';

const ProfilePage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('profile');
    const [userDetails, setUserDetails] = useState(null);
    const [userData, setUserData] = useState({
        name: '', firstName: '', middleName: '', lastName: '',
        email: '', phone: '', countryCode: '+91', image: ''
    });
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [imageLoading, setImageLoading] = useState(false);
    const [uploadedImageUrl, setUploadedImageUrl] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const imageUploadRef = useRef(null);

    useEffect(() => {
        const stored = JSON.parse(localStorage.getItem('userDetails'));
        if (!stored) { navigate('/'); return; }
        setUserDetails(stored);
        setUserData({
            name: stored.name || '',
            firstName: stored.firstName || '',
            middleName: stored.middleName || '',
            lastName: stored.lastName || '',
            email: stored.email || '',
            phone: stored.phoneNumber || '',
            countryCode: stored.countryCode || '+91',
            image: stored.image || ''
        });
    }, [navigate]);

    const getProfileImage = (img) => {
        if (!img || img === 'null' || img === 'undefined' || (typeof img === 'string' && img.trim() === '')) {
            return userProfile;
        }
        return img;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUserData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('image', file);
        setImageLoading(true);
        try {
            const response = await PropertiesService.UploadFiles(formData);
            if (response?.Location) {
                setUploadedImageUrl(response.Location);
            } else {
                setErrors(prev => ({ ...prev, image: 'Upload failed. Please try again.' }));
            }
        } catch {
            setErrors(prev => ({ ...prev, image: 'Upload error. Please try again.' }));
        } finally {
            setImageLoading(false);
        }
    };

    const validatePassword = (value) =>
        /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/.test(value);

    const saveProfile = async () => {
        const phonePattern = /^\d{10}$/;
        const newErrors = {};
        if (!userData.name) newErrors.name = 'Name is required';
        if (!userData.phone) newErrors.phone = 'Phone is required';
        else if (!phonePattern.test(userData.phone)) newErrors.phone = 'Must be 10 digits';
        if (!uploadedImageUrl && !userData.image) newErrors.image = 'Please upload a profile photo';
        if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
        setSaving(true);
        try {
            const obj = {
                userId: userDetails.user_id,
                name: userData.name,
                firstName: userData.firstName,
                middleName: userData.middleName,
                lastName: userData.lastName,
                phoneNumber: userData.phone,
                countryCode: userData.countryCode,
                image: uploadedImageUrl || userData.image,
                docs: ''
            };
            const response = await AuthService.editDoCityUserData(obj);
            if (response.success) {
                localStorage.setItem('userDetails', JSON.stringify(response.data.data));
                setSuccessMsg('Profile updated successfully!');
                setTimeout(() => { setSuccessMsg(''); navigate(-1); }, 1800);
            } else {
                setErrors({ general: response.error || 'Failed to update profile. Please try again.' });
            }
        } catch (e) {
            setErrors({ general: 'Failed to update profile. Please try again.' });
        } finally {
            setSaving(false);
        }
    };

    const changePassword = async () => {
        const newErrors = {};
        if (!password) newErrors.password = 'Current password is required';
        if (!newPassword) newErrors.newPassword = 'New password is required';
        else if (!validatePassword(newPassword)) newErrors.newPassword = 'Min 8 chars, include a number and special character';
        else if (password === newPassword) newErrors.newPassword = 'New password must differ from current';
        if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
        setSaving(true);
        try {
            const obj = { email: userDetails.email, oldPassword: password, newPassword };
            const response = await AuthService.UpdatePassword(obj);
            if (response.data !== undefined) {
                setSuccessMsg('Password changed successfully!');
                setPassword(''); setNewPassword('');
                setTimeout(() => setSuccessMsg(''), 2500);
            } else {
                setErrors({ password: 'Incorrect current password' });
            }
        } catch {
            setErrors({ general: 'Failed to change password. Please try again.' });
        } finally {
            setSaving(false);
        }
    };

    const previewImage = uploadedImageUrl || userData.image;

    return (
        <div className="profile-page">
            <div className="profile-page-container">
                {/* Back header */}
                <div className="profile-page-topbar">
                    <button className="profile-back-btn" onClick={() => navigate(-1)}>
                        <ArrowLeft size={18} />
                        <span>Back</span>
                    </button>
                </div>

                {/* Avatar + Name card */}
                <div className="profile-hero-card">
                    <div className="profile-avatar-wrap">
                        <img
                            src={getProfileImage(previewImage)}
                            alt="Profile"
                            className="profile-avatar-img"
                            onError={e => { e.target.onerror = null; e.target.src = userProfile; }}
                        />
                        <button
                            className="profile-avatar-edit-btn"
                            onClick={() => imageUploadRef.current?.click()}
                            title="Change photo"
                        >
                            {imageLoading ? <Loader size={14} className="profile-spin" /> : <Camera size={14} />}
                        </button>
                        <input
                            ref={imageUploadRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleImageUpload}
                        />
                    </div>
                    <div className="profile-hero-info">
                        <h2 className="profile-hero-name">{userDetails?.name || 'User'}</h2>
                        <p className="profile-hero-email">{userDetails?.email}</p>
                    </div>
                    {errors.image && <p className="profile-field-error" style={{ textAlign: 'center' }}>{errors.image}</p>}
                </div>

                {/* Tabs */}
                <div className="profile-tabs">
                    <button
                        className={`profile-tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('profile'); setErrors({}); setSuccessMsg(''); }}
                    >
                        <User size={15} /> Profile
                    </button>
                    <button
                        className={`profile-tab-btn ${activeTab === 'password' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('password'); setErrors({}); setSuccessMsg(''); }}
                    >
                        <Lock size={15} /> Change Password
                    </button>
                </div>

                {/* Form Card */}
                <div className="profile-form-card">
                    {successMsg && (
                        <div className="profile-success-banner">
                            <Check size={16} />
                            {successMsg}
                        </div>
                    )}
                    {errors.general && <p className="profile-field-error">{errors.general}</p>}

                    {activeTab === 'profile' ? (
                        <>
                            <div className="profile-form-grid">
                                <div className="profile-field">
                                    <label>Username / Name <span className="required">*</span></label>
                                    <input name="name" value={userData.name} onChange={handleChange} placeholder="Full name" />
                                    {errors.name && <span className="profile-field-error">{errors.name}</span>}
                                </div>
                                <div className="profile-field">
                                    <label>Phone <span className="required">*</span></label>
                                    <input name="phone" value={userData.phone} onChange={handleChange} placeholder="10-digit number" maxLength={10} />
                                    {errors.phone && <span className="profile-field-error">{errors.phone}</span>}
                                </div>
                                <div className="profile-field">
                                    <label>First Name</label>
                                    <input name="firstName" value={userData.firstName} onChange={handleChange} placeholder="First name" />
                                </div>
                                <div className="profile-field">
                                    <label>Middle Name</label>
                                    <input name="middleName" value={userData.middleName} onChange={handleChange} placeholder="Middle name" />
                                </div>
                                <div className="profile-field">
                                    <label>Last Name</label>
                                    <input name="lastName" value={userData.lastName} onChange={handleChange} placeholder="Last name" />
                                </div>
                                <div className="profile-field">
                                    <label>Email</label>
                                    <input name="email" value={userData.email} disabled placeholder="Email" className="profile-disabled" />
                                </div>
                            </div>
                            <div className="profile-form-actions">
                                <button className="profile-cancel-btn" onClick={() => navigate(-1)}>Cancel</button>
                                <button className="profile-save-btn" onClick={saveProfile} disabled={saving}>
                                    {saving ? <><Loader size={14} className="profile-spin" /> Saving...</> : <><Check size={14} /> Save Changes</>}
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="profile-form-grid profile-form-single">
                                <div className="profile-field">
                                    <label>Current Password <span className="required">*</span></label>
                                    <div className="profile-password-wrap">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={e => { setPassword(e.target.value); setErrors(p => ({ ...p, password: '' })); }}
                                            placeholder="Enter current password"
                                        />
                                        <button className="profile-eye-btn" onClick={() => setShowPassword(p => !p)} type="button">
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                    {errors.password && <span className="profile-field-error">{errors.password}</span>}
                                </div>
                                <div className="profile-field">
                                    <label>New Password <span className="required">*</span></label>
                                    <div className="profile-password-wrap">
                                        <input
                                            type={showNewPassword ? 'text' : 'password'}
                                            value={newPassword}
                                            onChange={e => { setNewPassword(e.target.value); setErrors(p => ({ ...p, newPassword: '' })); }}
                                            placeholder="Min 8 chars, 1 number, 1 special char"
                                        />
                                        <button className="profile-eye-btn" onClick={() => setShowNewPassword(p => !p)} type="button">
                                            {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                    {errors.newPassword && <span className="profile-field-error">{errors.newPassword}</span>}
                                </div>
                            </div>
                            <div className="profile-form-actions">
                                <button className="profile-cancel-btn" onClick={() => navigate(-1)}>Cancel</button>
                                <button className="profile-save-btn" onClick={changePassword} disabled={saving}>
                                    {saving ? <><Loader size={14} className="profile-spin" /> Updating...</> : <><Check size={14} /> Update Password</>}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
